import { EMPLOYEE_GENDER_OPTIONS } from '@/modules/Account/components/constants'
import {
    COUNTRIES,
    EMPLOYEE_MARRIAGE_STATUS,
    NATIONS,
    RELIGIONS,
} from '@/modules/Employee/constants'
import { getEmployeeInfo, updateEmployee } from '@/modules/Employee/services'
import { formatPGDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { GridHookForm, Loading } from 'vnetwork-ui'
import { FilterSelect } from '@/components/ui/filters'
import { validateStep } from '../../../Profile/schema'
import { EmployeeConfimationValues } from '@/modules/Employee/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getCurrentEmployee,
    updateCurrentEmployee,
} from '@/modules/Account/components/services'

interface PersonalProps {
    isSelf?: boolean
    employeeId?: number
}

// Build form default values from employee result (falls back to sensible defaults)
const buildPersonalFormValues = (employee: any = {}) => ({
    employee_name: employee?.employee_name || '',
    employee_email: employee?.employee_email || '',
    employee_phone_number: employee?.employee_phone_number || '',
    employee_birthday: employee?.employee_birthday || '',
    employee_birthplace: employee?.employee_birthplace || '',
    employee_gender: employee?.employee_gender || 'male',
    employee_marriage: employee?.employee_marriage || 'single',
    employee_nationality: employee?.employee_nationality || 'VN',
    employee_religion: employee?.employee_religion || 'Không',
    employee_nation: employee?.employee_nation || 'Kinh',
    employee_original_place: employee?.employee_original_place || '',
    // Liên hệ trong trường hợp khẩn cấp
    contact_homies:
        Array.isArray(employee?.contact_homies) &&
        employee.contact_homies.length > 0
            ? employee.contact_homies
            : [
                  {
                      contact_name: '',
                      contact_phone: '',
                      contact_relationship: '',
                  },
              ],
})

const Personal: React.FC<PersonalProps> = ({ isSelf = false, employeeId }) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () =>
            isSelf ? getCurrentEmployee() : getEmployeeInfo(Number(id)),
        enabled: !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: updateEmployeeMutation, isPending: isUpdatePending } =
        useMutationWithHandler(
            {
                mutationFn: updateEmployee,
            },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEES,
                successMessage: 'employee-update-success',
                errorMessage: 'error-occurred',
            }
        )

    const {
        mutateAsync: updateCurrentEmployeeMutation,
        isPending: isUpdateCurrentEmployeePending,
    } = useMutationWithHandler(
        {
            mutationFn: updateCurrentEmployee,
        },
        {
            reactQueryKeys: QUERY_KEYS.CURRENT_USER_PROFILE,
            successMessage: 'employee-update-success',
            errorMessage: 'error-occurred',
        }
    )

    const formControl = useForm<any>({
        defaultValues: buildPersonalFormValues(data?.result),
        mode: 'onChange',
        resolver: yupResolver(validateStep({ activeTab: 'Personal' }) as any),
    })

    useEffect(() => {
        formControl.reset(buildPersonalFormValues(data?.result))
    }, [data])

    const onSubmit = async (values: Partial<EmployeeConfimationValues>) => {
        const payload: Record<string, any> = {
            ...values,
            employee_birthday: values?.employee_birthday
                ? formatPGDate(values?.employee_birthday, 'YYYY-MM-DD')
                : null,
        }

        // Chỉ gửi contact_homies khi có nhập (Quan hệ hoặc SĐT). Tránh ghi đè
        // dữ liệu rỗng lên hồ sơ nhân viên chưa từng có liên hệ khẩn cấp.
        const contacts = Array.isArray(values?.contact_homies)
            ? (values.contact_homies as any[])
            : []
        const hasContact = contacts.some(
            (c) => c?.contact_relationship?.trim() || c?.contact_phone?.trim()
        )
        if (!hasContact) {
            delete payload.contact_homies
        }

        if (isSelf) {
            await updateCurrentEmployeeMutation(payload as any)
        } else {
            await updateEmployeeMutation({
                id: Number(id),
                employee: payload as any,
            })
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-6">
            {/* Personal Information Section */}
            <GridHookForm
                formControl={formControl}
                onSubmit={onSubmit}
                className="grid grid-cols-2"
                actions={[
                    {
                        title: t('save-changes'),
                        variant: 'contained',
                        type: 'submit',
                        loading:
                            isUpdatePending || isUpdateCurrentEmployeePending,
                        disabled:
                            isUpdatePending ||
                            isUpdateCurrentEmployeePending ||
                            !formControl.formState.isDirty,
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            label: t('employee_email'),
                            name: 'employee_email',
                            type: 'textfield',
                            placeholder: t('enter-email'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            label: t('employee_phone_number'),
                            name: 'employee_phone_number',
                            type: 'textfield',
                            placeholder: t('enter-phone-number'),
                        },
                    },
                    {
                        formItemProps: {
                            label: t('employee_birthday'),
                            name: 'employee_birthday',
                            type: 'datepicker',
                            placeholder: t('select-birthday'),
                        },
                    },
                    {
                        formItemProps: {
                            label: t('employees.birthplace'),
                            name: 'employee_birthplace',
                            type: 'textfield',
                            placeholder: t('enter-birthplace'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_gender"
                                label={t('employee_gender')}
                                placeholder={t('select-gender')}
                                options={EMPLOYEE_GENDER_OPTIONS(t)}
                                showClearButton={false}
                            />
                        ),
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_marriage"
                                label={t('employee_marriage')}
                                placeholder={t('select-marriage-status')}
                                options={EMPLOYEE_MARRIAGE_STATUS(t)}
                                showClearButton={false}
                            />
                        ),
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_nationality"
                                label={t('nationality')}
                                placeholder={t('enter-nationality')}
                                options={COUNTRIES}
                                showClearButton={false}
                            />
                        ),
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_religion"
                                label={t('religion')}
                                options={RELIGIONS}
                                showClearButton={false}
                            />
                        ),
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_nation"
                                label={t('nation')}
                                options={NATIONS}
                                showClearButton={false}
                            />
                        ),
                    },
                    {
                        formItemProps: {
                            label: t('employees.original_place'),
                            name: 'employee_original_place',
                            type: 'textfield',
                        },
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <p className="col-span-2 mt-2 text-sm font-semibold text-gray-900">
                                {t('employee:emergency_contact')}{' '}
                                <span className="text-red-500">*</span>
                            </p>
                        ),
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            label: t('employee:contact_relationship'),
                            name: 'contact_homies.0.contact_relationship',
                            type: 'textfield',
                            placeholder: t('employee:contact_relationship'),
                        },
                    },
                    {
                        formItemProps: {
                            label: t('employee:contact_phone'),
                            name: 'contact_homies.0.contact_phone',
                            type: 'textfield',
                            placeholder: t('employee:contact_phone'),
                        },
                    },
                ]}
            />
        </div>
    )
}

export default Personal
